/**
 * The fields of one own signature and the letter's ending drawn under them
 * while they are typed in. Used by the "my signatures and sender" window and
 * by the editor that opens on the signature inside a letter.
 *
 * Fields rather than one textarea, because the company card takes the name and
 * the lines into itself (`lib.ts`, `ownPreview`). Typed as free text they were
 * said twice: once in grey, once on the card.
 */
import { MailSignaturePreview } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import type { MailCardPreview } from "../../../../lib/mail";
import type { MyMailSettings, OwnSignature } from "../../../../lib/mailSignature";
import { Field, ToggleRow } from "../../settings/panel/fields";
import { ownPreview } from "./lib";

export function SignatureFields({
  sig,
  company,
  limits,
  onChange,
  autoFocus,
}: {
  sig: OwnSignature;
  company: MailCardPreview | null;
  limits: MyMailSettings["limits"];
  onChange: (part: Partial<OwnSignature>) => void;
  autoFocus?: boolean;
}) {
  const t = useT();
  const preview = ownPreview(sig, company);
  const empty = !preview.text && !preview.card;

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t("myMail.closing")}>
          <input
            className="crm-inp w-full"
            value={sig.closing}
            maxLength={limits.closing}
            placeholder={t("myMail.closingPlaceholder")}
            autoFocus={autoFocus}
            onChange={(e) => onChange({ closing: e.target.value })}
          />
        </Field>
        <Field label={t("myMail.name")}>
          <input
            className="crm-inp w-full"
            value={sig.name}
            maxLength={limits.name}
            autoComplete="name"
            onChange={(e) => onChange({ name: e.target.value })}
          />
        </Field>
      </div>

      <Field label={t("myMail.details")} hint={t("myMail.detailsHint", { n: limits.detailLines })}>
        {/* `crm-area`, not a textarea inside `crm-inp`: that pill is a fixed
            52px row and folds a textarea to one line. */}
        <textarea
          className="crm-area min-h-[72px]"
          rows={3}
          maxLength={limits.details}
          value={sig.details}
          placeholder={t("myMail.detailsPlaceholder")}
          onChange={(e) => onChange({ details: e.target.value })}
        />
      </Field>

      <ToggleRow
        label={t("myMail.card")}
        hint={company ? t("myMail.cardHint") : t("myMail.cardEmpty")}
        checked={sig.card}
        onChange={(v) => onChange({ card: v })}
      />

      <div>
        <p className="crm-lab !mt-0">{t("myMail.preview")}</p>
        {/* In the app's own colours, dark theme included: the same reading-pane
            classes the letter uses (`crm-mail-html` + `wa-sig-card`), whose
            dark rule hands the card's inks the page's own. */}
        <div className="crm-mail-html rounded-md border border-[var(--crm-hair)] px-5 py-4">
          {preview.text ? (
            <p className="m-0 whitespace-pre-wrap text-[13px] leading-[1.5] text-muted-foreground">{preview.text}</p>
          ) : (
            <div className="wa-sig-card">
              <MailSignaturePreview
                className="!rounded-none !border-0 !bg-transparent !p-0"
                closing={preview.card?.closing}
                name={preview.card?.name}
                details={preview.card?.details}
                accent={preview.card?.accent}
                logoUrl={preview.card?.logoUrl}
                emptyLabel={empty ? t("myMail.previewEmpty") : undefined}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
